/**
 * CompanySettings.jsx - Innstillinger for bedriften
 * 
 * v2.0 - Utvidet med:
 * - Organisasjons-Admin funksjonalitet
 * - Sentral sikkerhetsstyring (Server-Sync sti)
 * - Master Recovery Key generering
 * - Desktop-nedlasting konfigurasjon
 * 
 * KUN FULL TILGANG FOR company_admin
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { 
  Building2, 
  Mail, 
  Phone,
  MapPin,
  Shield,
  Key,
  Bell,
  Save,
  HardDrive,
  Server,
  Download,
  Lock,
  Copy,
  CheckCircle,
  AlertTriangle,
  Crown,
  Monitor,
  FolderSync,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

// Lokal lagring av organisasjonsinnstillinger
const ORG_SETTINGS_KEY = 'rettwerk_org_settings';

const getOrgSettings = () => {
  try {
    const stored = localStorage.getItem(ORG_SETTINGS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveOrgSettings = (settings) => {
  try {
    localStorage.setItem(ORG_SETTINGS_KEY, JSON.stringify(settings));
    return true;
  } catch {
    return false;
  }
};

export default function CompanySettings() {
  const { user } = useAuth();
  const { isAdmin, company } = useCompany();
  
  const [orgSettings, setOrgSettings] = useState(getOrgSettings);
  const [saving, setSaving] = useState(false);
  const [showRecoveryKey, setShowRecoveryKey] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  
  // Form states
  const [serverSyncPath, setServerSyncPath] = useState(orgSettings.serverSyncPath || '');
  const [enableDualWrite, setEnableDualWrite] = useState(orgSettings.enableDualWrite || false);
  const [autoBackup, setAutoBackup] = useState(orgSettings.autoBackup !== false);
  
  // Kopier til utklippstavle
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Kopiert til utklippstavle');
    } catch {
      toast.error('Kunne ikke kopiere');
    }
  };
  
  // Generer Master Recovery Key
  const generateRecoveryKey = async () => {
    setGeneratingKey(true);
    try {
      // Simuler nøkkelgenerering (i produksjon vil dette være kryptografisk sikker)
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const key = generateSecureKey();
      setRecoveryKey(key);
      setShowRecoveryKey(true);
      
      // Lagre hash av nøkkel (ikke selve nøkkelen)
      const keyHash = await hashKey(key);
      const newSettings = { ...orgSettings, recoveryKeyHash: keyHash, recoveryKeyCreated: new Date().toISOString() };
      saveOrgSettings(newSettings);
      setOrgSettings(newSettings);
      
      toast.success('Master Recovery Key generert', { description: 'Last ned og oppbevar sikkert!' });
    } catch (error) {
      toast.error('Kunne ikke generere nøkkel');
    } finally {
      setGeneratingKey(false);
    }
  };
  
  // Generer sikker nøkkel (forenklet - i produksjon bruk Web Crypto API)
  const generateSecureKey = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
  };
  
  // Hash nøkkel for lagring
  const hashKey = async (key) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(key);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  };
  
  // Last ned recovery key som fil
  const downloadRecoveryKey = () => {
    if (!recoveryKey) return;
    
    const content = `RettVerk Master Recovery Key
==============================
Organisasjon: ${company?.name || 'Min Bedrift'}
Generert: ${new Date().toLocaleString('nb-NO')}

VIKTIG: Oppbevar denne nøkkelen sikkert!
Den kan brukes til å gjenopprette krypterte Vaults.

Master Key:
${recoveryKey}

==============================
Dette dokumentet inneholder sensitiv informasjon.
Del aldri denne nøkkelen med uautoriserte personer.
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `RettVerk_Recovery_Key_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Recovery Key lastet ned');
  };
  
  // Lagre sikkerhetsinnstillinger
  const handleSaveSecuritySettings = async () => {
    setSaving(true);
    try {
      const newSettings = {
        ...orgSettings,
        serverSyncPath,
        enableDualWrite,
        autoBackup,
        updatedAt: new Date().toISOString()
      };
      
      if (saveOrgSettings(newSettings)) {
        setOrgSettings(newSettings);
        toast.success(
          <div className="flex items-center gap-2">
            <HardDrive className="w-4 h-4" />
            <span>Innstillinger lagret</span>
          </div>,
          { description: 'Lagret lokalt' }
        );
      } else {
        throw new Error('Kunne ikke lagre');
      }
    } catch (error) {
      toast.error('Kunne ikke lagre innstillinger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="company-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Innstillinger</h1>
          <p className="text-[#8899A8] mt-1">
            {isAdmin ? 'Administrer organisasjonens sikkerhet og innstillinger' : 'Se bedriftens innstillinger'}
          </p>
        </div>
        {isAdmin && (
          <span className="flex items-center gap-1.5 px-3 py-1.5 bg-[#B87333] text-white text-sm font-bold rounded-lg">
            <Crown className="w-4 h-4" />
            ORG ADMIN
          </span>
        )}
      </div>

      <div className="max-w-3xl space-y-6">
        
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* ORGANISASJONS-ADMIN FUNKSJONER (kun for admin) */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {isAdmin && (
          <>
            {/* Master Recovery Key */}
            <div className="bg-gradient-to-br from-[#1B2631] to-[#0F1419] border border-[#B87333]/30 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-lg bg-[#B87333]/20">
                  <Key className="w-5 h-5 text-[#B87333]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    Master Recovery Key
                    <span className="text-[10px] px-2 py-0.5 bg-[#B87333]/30 text-[#B87333] rounded font-bold">
                      KUN ADMIN
                    </span>
                  </h2>
                  <p className="text-sm text-[#8899A8]">
                    Gjenopprettingsnøkkel for alle krypterte Vaults i organisasjonen
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                {orgSettings.recoveryKeyCreated ? (
                  <div className="p-4 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-[#22c55e]" />
                      <span className="text-sm font-medium text-[#22c55e]">Recovery Key er generert</span>
                    </div>
                    <p className="text-xs text-[#8899A8]">
                      Generert: {new Date(orgSettings.recoveryKeyCreated).toLocaleString('nb-NO')}
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-[#f59e0b]" />
                      <span className="text-sm font-medium text-[#f59e0b]">Ingen Recovery Key</span>
                    </div>
                    <p className="text-xs text-[#8899A8]">
                      Vi anbefaler å generere en Master Recovery Key for sikkerhetskopiering.
                    </p>
                  </div>
                )}
                
                {/* Vis generert nøkkel */}
                {showRecoveryKey && recoveryKey && (
                  <div className="p-4 bg-[#0F1419] border border-[#2C3E50] rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-white">Din Master Recovery Key</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyToClipboard(recoveryKey)}
                          className="p-1.5 hover:bg-[#2C3E50] rounded text-[#8899A8] hover:text-white"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowRecoveryKey(!showRecoveryKey)}
                          className="p-1.5 hover:bg-[#2C3E50] rounded text-[#8899A8] hover:text-white"
                        >
                          {showRecoveryKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <code className="block p-3 bg-[#1B2631] rounded font-mono text-xs text-[#22c55e] break-all">
                      {recoveryKey}
                    </code>
                    <p className="text-xs text-red-400 mt-2">
                      ⚠️ Denne nøkkelen vises kun én gang. Last ned og oppbevar sikkert!
                    </p>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <button
                    onClick={generateRecoveryKey}
                    disabled={generatingKey}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#B87333] hover:bg-[#A06328] disabled:bg-[#2C3E50] text-white rounded-lg transition-colors"
                  >
                    {generatingKey ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4" />
                    )}
                    <span>{orgSettings.recoveryKeyCreated ? 'Generer ny nøkkel' : 'Generer Recovery Key'}</span>
                  </button>
                  
                  {recoveryKey && (
                    <button
                      onClick={downloadRecoveryKey}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Last ned</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Sentral sikkerhetsstyring - Server Sync */}
            <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-lg bg-[#3b82f6]/20">
                  <Server className="w-5 h-5 text-[#3b82f6]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    Sentral sikkerhetsstyring
                    <span className="text-[10px] px-2 py-0.5 bg-[#3b82f6]/20 text-[#3b82f6] rounded font-bold">
                      DUAL-WRITE
                    </span>
                  </h2>
                  <p className="text-sm text-[#8899A8]">
                    Konfigurer Server-Sync for alle ansatte i organisasjonen
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Enable Dual-Write */}
                <div className="flex items-center justify-between p-4 bg-[#0F1419] rounded-lg">
                  <div className="flex items-center gap-3">
                    <FolderSync className="w-5 h-5 text-[#8899A8]" />
                    <div>
                      <p className="text-sm font-medium text-white">Aktiver Dual-Write Sync</p>
                      <p className="text-xs text-[#8899A8]">Speiler alle krypterte filer til bedriftsserver</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setEnableDualWrite(!enableDualWrite)}
                    className={`w-11 h-6 rounded-full transition-colors ${
                      enableDualWrite ? 'bg-[#B87333]' : 'bg-[#2C3E50]'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      enableDualWrite ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                
                {/* Server-sti */}
                {enableDualWrite && (
                  <div>
                    <label className="block text-sm text-[#8899A8] mb-1.5">
                      Server-sync lagringssti (UNC eller lokal)
                    </label>
                    <input
                      type="text"
                      value={serverSyncPath}
                      onChange={(e) => setServerSyncPath(e.target.value)}
                      placeholder="\\server\share\rettwerk-backup eller /mnt/backup"
                      className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] focus:outline-none focus:border-[#B87333]"
                    />
                    <p className="text-xs text-[#8899A8] mt-1">
                      Denne stien vil gjelde for alle ansatte i organisasjonen.
                    </p>
                  </div>
                )}
                
                {/* Auto-backup */}
                <div className="flex items-center justify-between p-4 bg-[#0F1419] rounded-lg">
                  <div className="flex items-center gap-3">
                    <HardDrive className="w-5 h-5 text-[#8899A8]" />
                    <div>
                      <p className="text-sm font-medium text-white">Automatisk backup</p>
                      <p className="text-xs text-[#8899A8]">Daglig sikkerhetskopiering av lokale data</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setAutoBackup(!autoBackup)}
                    className={`w-11 h-6 rounded-full transition-colors ${
                      autoBackup ? 'bg-[#22c55e]' : 'bg-[#2C3E50]'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      autoBackup ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                
                {/* Info-boks */}
                <div className="p-4 bg-[#3b82f6]/10 border border-[#3b82f6]/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-white mb-1">
                        Låst konfigurasjon for ansatte
                      </p>
                      <p className="text-xs text-[#8899A8] leading-relaxed">
                        Ansatte kan ikke endre disse innstillingene. De vil automatisk 
                        bruke organisasjonens sikkerhetskonfigurasjon ved installasjon av RettVerk Desktop.
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleSaveSecuritySettings}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#B87333] hover:bg-[#A06328] disabled:bg-[#2C3E50] text-white rounded-lg transition-colors"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  <span>Lagre sikkerhetsinnstillinger</span>
                </button>
              </div>
            </div>

            {/* RettVerk Desktop - Admin */}
            <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-lg bg-[#B87333]/20">
                  <Monitor className="w-5 h-5 text-[#B87333]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">RettVerk Desktop</h2>
                  <p className="text-sm text-[#8899A8]">
                    Administrer nedlastingslenker for ansatte
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-[#B87333]/10 border border-[#B87333]/30 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-4 h-4 text-[#B87333]" />
                  <span className="text-sm font-medium text-[#B87333]">Kommer snart</span>
                </div>
                <p className="text-sm text-[#8899A8]">
                  Når RettVerk Desktop lanseres, vil du kunne generere personlige 
                  nedlastingslenker for hver ansatt, ferdig konfigurert med bedriftens 
                  sikkerhetsprotokoll.
                </p>
              </div>
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* GENERELLE INNSTILLINGER (alle brukere) */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        
        {/* Bedriftsinformasjon */}
        <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-lg bg-[#B87333]/20">
              <Building2 className="w-5 h-5 text-[#B87333]" />
            </div>
            <h2 className="text-lg font-semibold text-white">Bedriftsinformasjon</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-[#8899A8] mb-1">Bedriftsnavn</label>
              <input
                type="text"
                defaultValue={company?.name || user?.name || 'Min Bedrift AS'}
                disabled={!isAdmin}
                className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white disabled:opacity-60 focus:outline-none focus:border-[#B87333]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#8899A8] mb-1">Organisasjonsnummer</label>
              <input
                type="text"
                placeholder="123 456 789"
                disabled={!isAdmin}
                className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] disabled:opacity-60 focus:outline-none focus:border-[#B87333]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#8899A8] mb-1">E-post</label>
                <input
                  type="email"
                  defaultValue={user?.email}
                  disabled={!isAdmin}
                  className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white disabled:opacity-60 focus:outline-none focus:border-[#B87333]"
                />
              </div>
              <div>
                <label className="block text-sm text-[#8899A8] mb-1">Telefon</label>
                <input
                  type="tel"
                  placeholder="+47 123 45 678"
                  disabled={!isAdmin}
                  className="w-full px-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] disabled:opacity-60 focus:outline-none focus:border-[#B87333]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sikkerhet - Generell info */}
        <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-lg bg-[#22c55e]/20">
              <Shield className="w-5 h-5 text-[#22c55e]" />
            </div>
            <h2 className="text-lg font-semibold text-white">Sikkerhet</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#0F1419] rounded-lg">
              <div className="flex items-center gap-3">
                <Key className="w-5 h-5 text-[#8899A8]" />
                <div>
                  <p className="text-sm font-medium text-white">To-faktor autentisering</p>
                  <p className="text-xs text-[#8899A8]">Ekstra sikkerhet for din konto</p>
                </div>
              </div>
              <button className="px-3 py-1.5 text-sm bg-[#B87333] text-white rounded-lg hover:bg-[#A06328] transition-colors">
                Aktiver
              </button>
            </div>
            
            <div className="flex items-center gap-3 p-4 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-lg">
              <Shield className="w-5 h-5 text-[#22c55e]" />
              <div>
                <p className="text-sm font-medium text-[#22c55e]">Privacy-First Arkitektur</p>
                <p className="text-xs text-[#8899A8]">All data lagres lokalt på din enhet - aldri på våre servere</p>
              </div>
            </div>
            
            {/* Info om låst konfigurasjon for ikke-admin */}
            {!isAdmin && (
              <div className="flex items-center gap-3 p-4 bg-[#6366f1]/10 border border-[#6366f1]/30 rounded-lg">
                <Lock className="w-5 h-5 text-[#6366f1]" />
                <div>
                  <p className="text-sm font-medium text-[#6366f1]">Låst konfigurasjon</p>
                  <p className="text-xs text-[#8899A8]">
                    Sikkerhetsinnstillinger styres av organisasjonens administrator
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Varsler */}
        <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 rounded-lg bg-[#6366f1]/20">
              <Bell className="w-5 h-5 text-[#6366f1]" />
            </div>
            <h2 className="text-lg font-semibold text-white">Varsler</h2>
          </div>
          
          <div className="space-y-3">
            {[
              { label: 'E-postvarsler for frister', enabled: true },
              { label: 'Ukentlig aktivitetsrapport', enabled: false },
              { label: 'Varsler om nye funksjoner', enabled: true },
            ].map((setting, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-[#0F1419] rounded-lg">
                <span className="text-sm text-white">{setting.label}</span>
                <button 
                  className={`w-11 h-6 rounded-full transition-colors ${
                    setting.enabled ? 'bg-[#B87333]' : 'bg-[#2C3E50]'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    setting.enabled ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Lagre-knapp (kun for admin) */}
        {isAdmin && (
          <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#B87333] hover:bg-[#A06328] text-white rounded-lg font-medium transition-colors">
            <Save className="w-4 h-4" />
            Lagre endringer
          </button>
        )}
      </div>
    </div>
  );
}
